import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { ArrowLeft, Save, Loader2, Upload, Check, ExternalLink } from "lucide-react";

// -----------------------------------------------------------------------------
// SiteSettings — /admin/impostazioni
// Central admin page to edit all values that must be tweakable without code:
// logo, favicon, contacts, social, Statuto PDF, institutional photos, SEO,
// Analytics IDs. Data is persisted in the single-row `site_settings` table.
// -----------------------------------------------------------------------------

const Section = ({ title, subtitle, children }) => (
  <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
    <div className="mb-6">
      <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">{title}</div>
      {subtitle && <div className="text-sm text-slate-500">{subtitle}</div>}
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">{label}</div>
    {children}
    {hint && <div className="text-xs text-slate-400 mt-1.5">{hint}</div>}
  </label>
);

const Input = (p) => <input {...p} className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-2.5 outline-none focus:border-[#12964A]" />;
const Textarea = (p) => <textarea {...p} className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-2.5 outline-none focus:border-[#12964A]" />;

const UploadField = ({ value, onChange, folder, accept = "image/*", hint }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await adminApi.upload(file, folder);
      onChange(r.data.public_url);
    } catch (e) { alert("Errore upload"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-4">
      {value ? (
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {accept.includes("image") ? (
            <img src={value} alt="" className="w-14 h-14 rounded-xl object-cover border border-[#E5E7EB]" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-[#F0FAF3] flex items-center justify-center text-[#12964A] font-bold text-xs">PDF</div>
          )}
          <div className="min-w-0 flex-1">
            <a href={value} target="_blank" rel="noreferrer" className="text-sm text-[#12964A] hover:underline truncate flex items-center gap-1">
              Vedi <ExternalLink className="w-3 h-3" />
            </a>
            <button type="button" onClick={() => onChange("")} className="text-xs text-red-600 hover:underline">Rimuovi</button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-400 flex-1">Nessun file caricato</div>
      )}
      <label className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-[#12964A] rounded-full px-4 py-2 text-sm cursor-pointer bg-white">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {value ? "Sostituisci" : "Carica"}
        <input type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {hint && <div className="text-xs text-slate-400 ml-2">{hint}</div>}
    </div>
  );
};

const SiteSettings = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.getSiteSettings();
      setF({ ...r.data });
    } catch (e) { setError("Impossibile caricare le impostazioni"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const { id, updated_at, updated_by, ...payload } = f;
      await adminApi.updateSiteSettings(payload);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  if (!f) return <div className="pt-40 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#12964A]"><ArrowLeft className="w-4 h-4" /> Pannello</Link>
          <div className="flex items-center gap-2">
            {saved && <span className="text-sm text-[#0E7C3D] inline-flex items-center gap-1"><Check className="w-4 h-4" /> Salvato</span>}
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-50 text-white rounded-full px-5 py-2 text-sm" data-testid="settings-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Amministrazione</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Impostazioni del sito</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">Modifica logo, contatti, social, PDF, foto, SEO e Analytics senza toccare il codice.</p>
        </div>

        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

        <Section title="Identità" subtitle="Logo, favicon e nome del sito">
          <Field label="Logo del sito"><UploadField value={f.logo_url} onChange={(v) => set("logo_url", v)} folder="settings" /></Field>
          <Field label="Favicon"><UploadField value={f.favicon_url} onChange={(v) => set("favicon_url", v)} folder="settings" accept="image/*" hint=".ico o .png 32×32 consigliato" /></Field>
          <Field label="Nome del sito"><Input value={f.site_name || ""} onChange={(e) => set("site_name", e.target.value)} placeholder="es. CCN Sanluri" /></Field>
          <Field label="Sottotitolo"><Input value={f.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="Centro Commerciale Naturale" /></Field>
        </Section>

        <Section title="Contatti" subtitle="Recapiti pubblici del CCN">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Email"><Input type="email" value={f.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} placeholder="info@sanluriccn.it" /></Field>
            <Field label="Telefono"><Input value={f.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+39 070 ..." /></Field>
            <Field label="Indirizzo (riga completa)"><Input value={f.contact_address || ""} onChange={(e) => set("contact_address", e.target.value)} placeholder="Via ..., 09025 Sanluri" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitudine"><Input type="number" step="any" value={f.contact_latitude ?? ""} onChange={(e) => set("contact_latitude", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
              <Field label="Longitudine"><Input type="number" step="any" value={f.contact_longitude ?? ""} onChange={(e) => set("contact_longitude", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
            </div>
          </div>
        </Section>

        <Section title="Social" subtitle="Link ai profili ufficiali (URL completi)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Instagram"><Input value={f.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/..." /></Field>
            <Field label="Facebook"><Input value={f.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://facebook.com/..." /></Field>
            <Field label="YouTube"><Input value={f.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/..." /></Field>
            <Field label="TikTok"><Input value={f.tiktok_url || ""} onChange={(e) => set("tiktok_url", e.target.value)} placeholder="https://tiktok.com/@..." /></Field>
            <Field label="LinkedIn"><Input value={f.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/company/..." /></Field>
          </div>
        </Section>

        <Section title="Media istituzionale" subtitle="PDF Statuto e foto Direttivo">
          <Field label="PDF Statuto dell'Associazione" hint="Quando caricato, apparirà automaticamente il bottone 'Scarica lo Statuto' nella pagina Chi siamo."><UploadField value={f.statute_pdf_url} onChange={(v) => set("statute_pdf_url", v)} folder="settings" accept="application/pdf" /></Field>
          <Field label="Foto del Direttivo" hint="Sostituirà la foto attuale nella pagina Chi siamo."><UploadField value={f.direttivo_photo_url} onChange={(v) => set("direttivo_photo_url", v)} folder="settings" /></Field>
        </Section>

        <Section title="SEO globale" subtitle="Meta tag di default per il sito">
          <Field label="Titolo SEO (max 60 caratteri)"><Input maxLength={90} value={f.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} placeholder="CCN Sanluri · Centro Commerciale Naturale" /></Field>
          <Field label="Descrizione SEO (max 160 caratteri)"><Textarea rows={3} maxLength={200} value={f.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} placeholder="Scopri le attività, gli eventi e le iniziative del centro storico di Sanluri..." /></Field>
          <Field label="Immagine Open Graph (per condivisioni social)"><UploadField value={f.seo_og_image} onChange={(v) => set("seo_og_image", v)} folder="settings" hint="1200×630 px consigliato" /></Field>
        </Section>

        <Section title="Analytics" subtitle="Codici di tracciamento (opzionali)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Google Analytics 4 ID" hint="Es. G-XXXXXXXXXX"><Input value={f.ga4_id || ""} onChange={(e) => set("ga4_id", e.target.value)} placeholder="G-..." /></Field>
            <Field label="Google Tag Manager ID" hint="Es. GTM-XXXXXXX"><Input value={f.gtm_id || ""} onChange={(e) => set("gtm_id", e.target.value)} placeholder="GTM-..." /></Field>
            <Field label="Meta Pixel ID" hint="Es. 123456789012345"><Input value={f.meta_pixel_id || ""} onChange={(e) => set("meta_pixel_id", e.target.value)} placeholder="Meta Pixel" /></Field>
            <Field label="Microsoft Clarity ID" hint="Es. abcd1234ef"><Input value={f.clarity_id || ""} onChange={(e) => set("clarity_id", e.target.value)} placeholder="Clarity project ID" /></Field>
          </div>
          <p className="text-xs text-slate-500 mt-3">Gli script vengono iniettati automaticamente sulle pagine pubbliche appena salvi. Lascia vuoto un campo per disattivare il singolo tracker.</p>
        </Section>

        <div className="text-xs text-slate-400 text-center pt-4">
          Ultimo aggiornamento: {f.updated_at ? new Date(f.updated_at).toLocaleString("it-IT") : "—"}
          {f.updated_by && <> · da <strong>{f.updated_by}</strong></>}
        </div>
      </main>
    </div>
  );
};

export default SiteSettings;
